import { useEffect, useRef, useState } from 'react';
import { Graph } from '../../shared/graph.js';
import { getPaintOrder } from '../../shared/layers.js';
import { GRID_SIZE, snap } from '../../shared/geometry.js';

export default function usePaletteCreation({ graph, stageRef, view, readOnly, changed, onInteraction, notify }) {
  const [ghost, setGhost] = useState(null);
  const gesture = useRef(null), suppressClick = useRef(false), current = useRef(null);
  current.current = { graph, stageRef, view, readOnly, changed, onInteraction, notify };
  function proposal(type, clientX, clientY, shiftKey = false, root = false) {
    const ctx = current.current, stage = ctx.stageRef.current, box = stage?.getBoundingClientRect();
    if (!box || clientX < box.left || clientX > box.right || clientY < box.top || clientY > box.bottom) return { inside: false, clientX, clientY, type };
    const p = { x: (clientX - box.left - ctx.view.x) / ctx.view.zoom, y: (clientY - box.top - ctx.view.y) / ctx.view.zoom };
    const w = type === 'container' ? 240 : 160, h = type === 'container' ? 180 : 72;
    const parent = !root && getPaintOrder(ctx.graph.state).slice().reverse().filter(entry => entry.kind === 'node').map(entry => entry.item).find(n => n.type === 'container' && p.x >= n.x && p.x <= n.x + n.w && p.y >= n.y + 40 && p.y <= n.y + n.h);
    let x = snap(p.x - w / 2, shiftKey), y = snap(p.y - h / 2, shiftKey);
    if (parent) {
      const minimum = value => shiftKey ? value : Math.ceil(value / GRID_SIZE) * GRID_SIZE;
      x = Math.max(x, minimum(parent.x + parent.padding)); y = Math.max(y, minimum(parent.y + 40 + parent.padding));
    }
    const settings = { x, y, parentId: parent?.id || null };
    const test = new Graph(ctx.graph.snapshot()), result = test.addNode(type, settings, { free: true });
    return { inside: true, clientX, clientY, type, settings, valid: result.ok, error: result.error, parentId: parent?.id || null, parentTitle: parent?.title, node: result.ok ? test.getNode(result.id) : { x, y, w, h } };
  }
  function cancel() { gesture.current = null; setGhost(null); current.current.onInteraction?.(false); }
  useEffect(() => {
    const blur = () => { if (gesture.current) cancel(); };
    const escape = event => { if (event.key === 'Escape' && gesture.current) { event.preventDefault(); cancel(); suppressClick.current = true; } };
    window.addEventListener('blur', blur); window.addEventListener('keydown', escape);
    return () => { window.removeEventListener('blur', blur); window.removeEventListener('keydown', escape); };
  }, []);
  useEffect(() => { if (readOnly) cancel(); }, [readOnly]);
  function click(type) {
    if (suppressClick.current) { suppressClick.current = false; return; }
    const ctx = current.current, box = ctx.stageRef.current?.getBoundingClientRect();
    if (ctx.readOnly || !box) return;
    const proposed = proposal(type, box.left + box.width / 2, box.top + box.height / 2, false, true);
    if (!proposed.inside) return;
    for (let attempt = 0; attempt < 12 && ctx.graph.state.nodes.some(n => Math.abs(n.x - proposed.settings.x) < 12 && Math.abs(n.y - proposed.settings.y) < 12); attempt++) { proposed.settings.x += 20; proposed.settings.y += 20; }
    ctx.changed(ctx.graph.addNode(type, proposed.settings), `已添加${type === 'container' ? '区域' : '服务'}；双击文字可直接编辑`);
  }
  function props(type) {
    return {
      'data-testid': `palette-${type}`, title: '点击添加到画布中心，或拖到画布指定位置',
      onClick: () => click(type),
      onPointerDown: event => {
        if (current.current.readOnly || event.button !== 0) return;
        gesture.current = { type, x: event.clientX, y: event.clientY, moved: false };
        event.currentTarget.setPointerCapture(event.pointerId);
      },
      onPointerMove: event => {
        const active = gesture.current; if (!active) return;
        if (!active.moved && Math.hypot(event.clientX - active.x, event.clientY - active.y) < 5) return;
        if (!active.moved) { active.moved = true; current.current.onInteraction?.(true); }
        event.preventDefault(); setGhost(proposal(type, event.clientX, event.clientY, event.shiftKey));
      },
      onPointerUp: event => {
        const active = gesture.current; gesture.current = null; setGhost(null);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        if (!active?.moved) return;
        suppressClick.current = true; setTimeout(() => { suppressClick.current = false; }, 0);
        const ctx = current.current, target = proposal(type, event.clientX, event.clientY, event.shiftKey);
        if (!ctx.readOnly && target.inside && target.valid) ctx.changed(ctx.graph.addNode(type, target.settings, { free: true }), target.parentTitle ? `已在「${target.parentTitle}」中创建` : '已在画布落点创建');
        else if (target.inside && !target.valid) ctx.notify(target.error, true);
        ctx.onInteraction?.(false);
      },
      onPointerCancel: cancel,
      onLostPointerCapture: () => { if (gesture.current) cancel(); },
      onDragStart: event => event.preventDefault()
    };
  }
  return { ghost, props, click };
}
